import type { ManifestFolder } from "../types";

/** URL prefix that namespaces folder routes so they never collide with article slugs. */
export const FOLDER_PATH_PREFIX = "/f/";

/** Legacy hash prefix kept only for redirecting old `#folder-…` share links. */
const LEGACY_FOLDER_HASH_PREFIX = "folder-";

/**
 * Turn a folder name into a URL-friendly slug: lowercase, accent-stripped,
 * non-alphanumerics collapsed to single hyphens, trimmed. Falls back to
 * `"folder"` so an emoji-only or symbol-only name still yields a valid token.
 */
export function slugifyFolderName(name: string): string {
  const base = name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return base || "folder";
}

export interface FolderPathMaps {
  /** uuid → slug segment, unique among siblings (e.g. `triggers`). */
  segmentByUuid: Map<string, string>;
  /** uuid → full nested path, ancestors first (e.g. `agentstudio/journey/triggers`). */
  pathByUuid: Map<string, string>;
  /** full nested path → uuid, for resolving an incoming URL. */
  uuidByPath: Map<string, string>;
}

/**
 * Build stable, collision-free nested path maps for every folder.
 *
 * Each folder gets a slug that is unique among its siblings (re-checking
 * candidates so a numeric suffix can't collide with a sibling literally named
 * "<base> 2"), then the full path is the chain of ancestor segments joined by
 * `/`. Siblings are processed by position then uuid so a folder always wins the
 * same segment across reloads.
 */
export function buildFolderPathMaps(
  folders: ManifestFolder[],
): FolderPathMaps {
  const folderByUuid = new Map<string, ManifestFolder>();
  const childrenByParent = new Map<string | null, ManifestFolder[]>();
  for (const folder of folders) {
    folderByUuid.set(folder.uuid, folder);
    const key = folder.parent_folder_uuid ?? null;
    const arr = childrenByParent.get(key) ?? [];
    arr.push(folder);
    childrenByParent.set(key, arr);
  }

  const segmentByUuid = new Map<string, string>();
  for (const siblings of childrenByParent.values()) {
    const ordered = [...siblings].sort(
      (a, b) => a.position - b.position || a.uuid.localeCompare(b.uuid),
    );
    const used = new Set<string>();
    for (const folder of ordered) {
      const base = slugifyFolderName(folder.name);
      let slug = base;
      let n = 2;
      while (used.has(slug)) {
        slug = `${base}-${n}`;
        n += 1;
      }
      used.add(slug);
      segmentByUuid.set(folder.uuid, slug);
    }
  }

  const pathByUuid = new Map<string, string>();
  const uuidByPath = new Map<string, string>();
  for (const folder of folders) {
    const segments: string[] = [];
    const seen = new Set<string>();
    let current: ManifestFolder | undefined = folder;
    while (current && !seen.has(current.uuid)) {
      seen.add(current.uuid);
      segments.unshift(segmentByUuid.get(current.uuid) ?? current.uuid);
      current = current.parent_folder_uuid
        ? folderByUuid.get(current.parent_folder_uuid)
        : undefined;
    }
    const path = segments.join("/");
    pathByUuid.set(folder.uuid, path);
    uuidByPath.set(path, folder.uuid);
  }

  return { segmentByUuid, pathByUuid, uuidByPath };
}

/** uuid → nested path (e.g. `agentstudio/journey`), falling back to the uuid. */
export function folderUrlPath(maps: FolderPathMaps, uuid: string): string {
  return maps.pathByUuid.get(uuid) ?? uuid;
}

/** uuid → full href (e.g. `/f/agentstudio/journey`). */
export function folderHref(maps: FolderPathMaps, uuid: string): string {
  return `${FOLDER_PATH_PREFIX}${folderUrlPath(maps, uuid)}`;
}

/** Resolve a nested path (without the `/f/` prefix) back to a folder uuid. */
export function resolveFolderUrlPath(
  maps: FolderPathMaps,
  path: string | null,
): string | null {
  if (!path) return null;
  const normalized = path.replace(/^\/+|\/+$/g, "");
  return maps.uuidByPath.get(normalized) ?? null;
}

/**
 * Extract the folder path from a location pathname, or `null` when the path
 * isn't a folder route. `/f/a/b` → `a/b`; `/f` or `/f/` → `null`.
 */
export function getFolderPathFromPathname(pathname: string): string | null {
  if (!pathname.startsWith(FOLDER_PATH_PREFIX)) return null;
  const rest = pathname.slice(FOLDER_PATH_PREFIX.length).replace(/\/+$/, "");
  return rest || null;
}

/**
 * Resolve a legacy `#folder-…` hash token (a public slug from the previous
 * hash scheme, or a raw uuid) back to a folder uuid so old shared links can
 * be redirected to the new `/f/…` path.
 */
export function resolveLegacyFolderToken(
  hash: string,
  maps: FolderPathMaps,
  folders: ManifestFolder[],
): string | null {
  if (!hash.startsWith(`#${LEGACY_FOLDER_HASH_PREFIX}`)) return null;
  const token = hash.slice(`#${LEGACY_FOLDER_HASH_PREFIX}`.length);
  if (!token) return null;

  // Raw uuid (the original bug) or new nested path both resolve directly.
  if (maps.pathByUuid.has(token)) return token;
  const byPath = resolveFolderUrlPath(maps, token);
  if (byPath) return byPath;

  // Old single-segment slug: match a folder whose own segment slug matches.
  for (const folder of folders) {
    if (maps.segmentByUuid.get(folder.uuid) === token) return folder.uuid;
  }
  return null;
}
